/**
 * Integer-cent arithmetic that is not a single amount's parse or render.
 *
 * Amounts in different currencies are never summed (D21). Every total here is
 * therefore per ISO code, and a caller that wants one figure has to say which
 * currency it means.
 *
 * `test/moneyFloatGuard.test.ts` allows `/ 100` and `* 100` in this file, in
 * `parseMoney.ts` and in `Money.tsx`, and nowhere else.
 */

export type CurrencyTotal = { currency: string; cents: number }

function assertInt(n: number, what: string): void {
  if (!Number.isSafeInteger(n)) throw new Error(`${what}: expected a safe integer, got ${String(n)}`)
}

/** Sums per ISO code, in first-seen order. Never sums across currencies (D21). */
export function totalsByCurrency<T>(
  rows: readonly T[],
  cents: (r: T) => number,
  currency: (r: T) => string,
): CurrencyTotal[] {
  const byCode = new Map<string, number>()
  for (const r of rows) {
    const c = cents(r)
    assertInt(c, 'totalsByCurrency')
    const code = currency(r)
    byCode.set(code, (byCode.get(code) ?? 0) + c)
  }
  return [...byCode].map(([code, total]) => ({ currency: code, cents: total }))
}

/**
 * Integer average, rounded half away from zero. Remainder comparison rather
 * than `Math.round(total / n)`: `Math.round(-500.5)` is -500, so the float form
 * rounds a negative half toward zero and a positive half away from it.
 */
export function averageCents(totalCents: number, n: number): number {
  assertInt(totalCents, 'averageCents')
  assertInt(n, 'averageCents')
  if (n <= 0) throw new Error(`averageCents: n must be positive, got ${n}`)
  const quotient = Math.trunc(totalCents / n)
  const remainder = totalCents - quotient * n
  if (Math.abs(remainder) * 2 >= n) return quotient + (totalCents < 0 ? -1 : 1)
  return quotient
}
